import { createApi } from "@reduxjs/toolkit/query/react"
import type { MonthlyExtractFileShare, MonthlyExtractRunAttrs } from "shared/monthlyExtractRun"
import { baseQuery } from "../baseQuerySl2"

/** One run as stored by LOCKER-468 (`apps/sl2/shared/monthlyExtractRun.d.ts`). */
export type MonthlyExtractRunDto = MonthlyExtractRunAttrs & {
  id: string
  createdAt: string
  updatedAt?: string
}

/** A list row never carries a PIN — only the one-run read does. */
export type MonthlyExtractRunListItemDto = Omit<MonthlyExtractRunDto, "shares"> & {
  shares?: Record<string, Omit<MonthlyExtractFileShare, "sharePin">>
}

export type ManifestTabDto = {
  name: string
  rowCount: number
  columns: string[]
  /** The first ≤50 rows, as rendered into the workbook (N2 — the preview source); every row when `complete`. */
  preview: Record<string, unknown>[]
  /** LOCKER-505: `preview` is the whole sheet (the Documentation tab). */
  complete?: true
}

/** `manifest.json` (LOCKER-468 contract §3); schemaVersion 2 since LOCKER-500 dropped the zip. */
export type MonthlyExtractManifestDto = {
  schemaVersion: number
  runId: string
  generatedAt: string
  summary: {
    period: string
    model: string
    windowMode: string
    window: { startUtc: string; endUtc: string }
    capacityWindow: { startUtc: string; endUtc: string }
    branches: number
    reservations: number
    branchesAtCapacity: number
    rowCounts: Record<string, number>
    warnings: Record<string, unknown[]>
  }
  workbooks: { filename: string; key: string; bytes: number; tabs: ManifestTabDto[] }[]
}

/** LOCKER-496 — the month window of a run. */
export type MonthlyExtractWindow = NonNullable<MonthlyExtractRunDto["window"]>

export type TriggerResultDto = {
  period: string
  window: MonthlyExtractWindow
  /** Null when the extract has not recorded its run yet (queued behind a running one). */
  runId: string | null
  status: MonthlyExtractRunDto["status"] | "queued"
}

/** LOCKER-500 — the share of one workbook, keyed by its S3 key. */
export type ShareDto = { key: string; share: MonthlyExtractFileShare }

/**
 * Client for the super-admin monthly-extract routes (LOCKER-469 DoD5), all on
 * fnThingsV2 behind `superAdminOnly`.
 */
export const monthlyExtractApi = createApi({
  reducerPath: "monthlyExtractApi",
  baseQuery,
  tagTypes: ["MonthlyExtractRun"],
  endpoints: (builder) => ({
    getMonthlyExtractRuns: builder.query<
      { data: MonthlyExtractRunListItemDto[]; meta: { nextToken?: string } },
      { limit?: number } | void
    >({
      query: (args) => ({
        url: "monthly-extracts",
        params: { limit: args?.limit ?? 100 },
      }),
      providesTags: [{ type: "MonthlyExtractRun", id: "LIST" }],
    }),

    getMonthlyExtractRun: builder.query<
      {
        data: {
          run: MonthlyExtractRunDto
          manifest: MonthlyExtractManifestDto | null
          manifestError?: string
        }
      },
      string
    >({
      query: (runId) => ({ url: `monthly-extracts/${encodeURIComponent(runId)}` }),
      providesTags: (_r, _e, runId) => [{ type: "MonthlyExtractRun", id: runId }],
    }),

    /** `keepUnusedDataFor: 0` — the URL expires in five minutes; never serve a stale one. */
    getMonthlyExtractObjectUrl: builder.query<
      { data: { url: string; expiresInSeconds: number; kind: string; filename: string } },
      { runId: string; key: string }
    >({
      query: ({ runId, key }) => ({ url: "monthly-extracts/object", params: { runId, key } }),
      keepUnusedDataFor: 0,
    }),

    triggerMonthlyExtract: builder.mutation<
      { data: TriggerResultDto },
      { year: number; month: number; window: MonthlyExtractWindow }
    >({
      query: (body) => ({ url: "monthly-extracts", method: "POST", body }),
      invalidatesTags: [{ type: "MonthlyExtractRun", id: "LIST" }],
    }),

    /** LOCKER-500 DoD4 — a PIN share of one workbook; replaces that workbook's share only. */
    createMonthlyExtractShare: builder.mutation<{ data: ShareDto }, { runId: string; key: string }>({
      query: ({ runId, key }) => ({
        url: `monthly-extracts/${encodeURIComponent(runId)}/share`,
        method: "POST",
        body: { key },
      }),
      invalidatesTags: (_r, _e, { runId }) => [
        { type: "MonthlyExtractRun", id: runId },
        { type: "MonthlyExtractRun", id: "LIST" },
      ],
    }),
  }),
})

export const {
  useGetMonthlyExtractRunsQuery,
  useGetMonthlyExtractRunQuery,
  useLazyGetMonthlyExtractObjectUrlQuery,
  useTriggerMonthlyExtractMutation,
  useCreateMonthlyExtractShareMutation,
} = monthlyExtractApi
